// db-products.js
window.productMap = window.productMap || {};

async function fetchAllProducts() {
  let products = [];
  let hasNextPage = true;
  let endCursor = null;

  const query = `
    query FetchProducts($cursor: String) {
      products(first: 100, after: $cursor) {
        pageInfo { hasNextPage }
        edges {
          cursor
          node {
            id title vendor productType availableForSale tags onlineStoreUrl createdAt
            featuredImage { url altText }
            variants(first: 1) {
              edges { node { availableForSale price { amount currencyCode } } }
            }
          }
        }
      }
    }
  `;
  
  let pageCount = 0;
  while (hasNextPage) {
    if (++pageCount > MAX_PAGES) {
      throw new Error(`Exceeded max pagination pages (${MAX_PAGES}).`);
    }
    try {
  const res = await fetch(GRAPHQL_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ query, variables: { cursor: endCursor } }),
  });

      const json = await res.json();
      if (!json.data || !json.data.products) {
        throw new Error("Missing product data from API.");
      }

      const edges = json.data.products.edges;
      hasNextPage = json.data.products.pageInfo.hasNextPage;
      endCursor = edges.length ? edges[edges.length - 1].cursor : null;

      products.push(...edges.map((edge) => edge.node));
   } catch (err) {
     console.warn(`Fetch page ${pageCount} failed—retrying after back-off…`, err);
     // exponential back-off before retry
     const delay = BASE_BACKOFF_MS * Math.pow(2, pageCount - 1);
     await new Promise((resolve) => setTimeout(resolve, delay));
     continue;
   }
  }
  return products;
}

/**
 * Fetch only the products matching a given product type.
 * @param {string} type – e.g. "Mousepads"
 * @returns {Promise<Array>} list of product nodes
 */
async function fetchProductsByType(type) {
  let products = [],
    hasNextPage = true,
    endCursor = null;

  const query = `
    query($cursor:String) {
      products(first:100, after:$cursor, query:"product_type:${type}") {
        pageInfo { hasNextPage }
        edges {
          cursor
          node {
            id
            title
            vendor
            productType
            availableForSale
            tags
            onlineStoreUrl
            createdAt
            featuredImage { url altText }
            variants(first:1) {
              edges { node {
                availableForSale
                price { amount currencyCode }
              } }
            }
          }
        }
      }
    }
  `;
  
  let pageCount = 0;
  while (hasNextPage) {
    if (++pageCount > MAX_PAGES) {
      throw new Error(`Exceeded max pages (${MAX_PAGES}) fetching type "${type}".`);
    }
    try {
      const res = await fetch(GRAPHQL_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query, variables: { cursor: endCursor } }),
      });
      const json = await res.json();
      const data = json.data.products;
      hasNextPage = data.pageInfo.hasNextPage;
      const edges = data.edges;
      endCursor = edges.length ? edges[edges.length - 1].cursor : null;
      products.push(...edges.map((e) => e.node));
     } catch (err) {
       console.warn(`Fetch page ${pageCount} for type "${type}" failed—retrying…`, err);
       const delay = BASE_BACKOFF_MS * Math.pow(2, pageCount - 1);
       await new Promise((resolve) => setTimeout(resolve, delay));
       continue;
     }
  }

  return products;
}

function updateProductCount(products, productType) {
  const filteredProducts = products.filter(
    (product) => product.productType === productType
  );
  const countContainer = document.getElementById("product-count-container");
  if (countContainer) {
    countContainer.textContent = `${filteredProducts.length} ${productType}`;
  }
}

function getTagFieldsForType(type) {
  const fields = {
    Mice: [
      { label: "Shape", prefix: "Shape_" },
      { label: "Size", prefix: "P-Size__" },
      { label: "Dims", prefix: "Dims__" },
      { label: "Shell", prefix: "Shell__" },
      { label: "Grip Style", prefix: "P-Grip Style__" },
      { label: "Buttons", prefix: "P-Buttons__" },
      { label: "Switches", prefix: "M-Switch__" },
      { label: "Weight", prefix: "P-Weight__" },
      { label: "Sensor Model", prefix: "Sensor Model_" },
      { label: "Sensor Type", prefix: "Sensor Type_" },
      { label: "Polling Rate", prefix: "Polling Rate__" },
      { label: "CPI/DPI", prefix: "CPI/DPI__" },
      { label: "Adjustable DPI", prefix: "Adjustable DPI__" },
      { label: "Connection", prefix: "Connection__" },
      { label: "Battery Life", prefix: "Battery Life__" },
      { label: "Cable Length", prefix: "Cable Length__" },
      { label: "Cable Material", prefix: "Cable Material__" },
      { label: "Warranty", prefix: "Warranty__" },
      { label: "In the box", prefix: "Box__" },
    ],
    Mousepads: [
      { label: "Dims", prefix: "Dims__" },
      { label: "Surface", prefix: "Surface Material_" },
      { label: "Glide Type", prefix: "Surface Type__" },
      { label: "Firmness", prefix: "Firmness__" },
     // { label: "Static Friction", prefix: "Static Friction__" },
     // { label: "Dynamic Friction", prefix: "Dynamic Friction__" },
      { label: "Coating", prefix: "Surface Coating__" },
      { label: "Texture", prefix: "Surface Texture__" },
      { label: "Colour", prefix: "Colour__" },
      { label: "Graphics", prefix: "Graphics__" },
      { label: "Base", prefix: "Base Material__" },
      { label: "Stitched", prefix: "Stitched Edges_" },
      { label: "Lighting", prefix: "Lighting_" },
      { label: "Game Type", prefix: "Game Type__" },
      { label: "Washable", prefix: "Washable__" },
      { label: "Packaged", prefix: "Packaging Type__" },
      { label: "Warranty", prefix: "Warranty__" },
    ],
    "Mouse Feet": [
      { label: "Model Comp.", prefix: "Compatible with__" },
      { label: "Thickness", prefix: "Thickness__" },
      { label: "Dot Dia.", prefix: "Dot_" },
      { label: "Material", prefix: "Material__" },
      { label: "Glide Type", prefix: "Glide__" },
      { label: "Surface Comp.", prefix: "Surface Comp_" },
      { label: "Adhesive", prefix: "Adhesive_" },
      { label: "Rounded Edges", prefix: "Rounded Edges__" },
      { label: "Colour", prefix: "Colour__" },
      { label: "In the pack", prefix: "Box__" },
    ],
    "Grip Tape": [
      { label: "Compatibility", prefix: "Compatible with__" },
      { label: "Colour", prefix: "Colour__" },
      { label: "Thickness", prefix: "Thickness__" },
      { label: "Dims", prefix: "Dims__" },
      { label: "Material", prefix: "Material__" },
      { label: "Flatten Edges", prefix: "Flatten Edges__" },
    ],
    "Arm Sleeves": [
      { label: "Size", prefix: "P-Size__" },
      { label: "Material", prefix: "Material_" },
      { label: "Style", prefix: "Style_" },
      { label: "Sleeve Length", prefix: "Sleeve-Length__" },
      { label: "Colour", prefix: "Colour__" },
      { label: "In the box", prefix: "Box__" },
    ],
  };
  return fields[type] || [];
}

const tooltipHints = {
  "Match %":
    "How closely two products match eacother based the following features:\n 1. Dimensions\n 2. Glide Type\n 3. Materials (Surface & Base)\n 4. Coating\n 5. Texture\n 6. Firmness\n 7. Edging\n 8. Lighting",
  "Thickness (mm)": "Surface or padding thickness; affects comfort and glide feel.",
  Surface: "Material used on the top layer; affects texture and glide.",
  "Glide Type": "General glide behavior (e.g., control vs. speed).",
  Firmness: "How soft or hard the pad feels when pressed.",
  "Static Friction": "Initial resistance when starting a mouse movement.",
  "Dynamic Friction": "Resistance during ongoing movement.",
  Coating: "Surface treatment; impacts slickness and durability.",
  Texture: "Describes micro-texture; affects mouse sensor behavior and feel.",
  Graphics: "Design or print on the surface.",
  Base: "Material used on the underside; affects grip and slide.",
  "Game Type": "Recommended genre(s) (e.g., FPS, MOBA etc).",
  Washable: "Whether the product can be machine washed or wipe cleaned.",
  Packaged: "How the product is packaged (e.g., rolled, flat).",
};

const COMMENTS_ICON_URL = "https://cdn.shopify.com/s/files/1/0102/8426/9615/files/square-chat-4_24x24_1x_765fd7f3-471c-46a9-9ae1-57b0a64a3f22.svg?v=1756811988";

const colourMap = window.DB_COLOUR_MAP;

function calculateProductMatchPercent(productA, productB) {
  const tagFields = matchPercentFieldsByType[productA.productType] || [];
  let total = 0;
  let match = 0;
  tagFields.forEach((field) => {
    const tagA = productA.tags.find((t) => t.startsWith(field.prefix));
    const tagB = productB.tags.find((t) => t.startsWith(field.prefix));
    const valA = tagA
      ? tagA.replace(field.prefix, "").toLowerCase().trim()
      : "";
    const valB = tagB
      ? tagB.replace(field.prefix, "").toLowerCase().trim()
      : "";
    if (valA || valB) {
      total++;
      if (valA && valB && valA === valB) match++;
    }
  });
  return total === 0 ? 0 : Math.round((match / total) * 100);
}

function openProductModalById(id) {
  const product = productMap[id];
  if (product) {
    openProductModal(product);
  } else {
    console.warn("Product not found for ID:", id);
  }
}

function renderProductsTable(products, productType) {
  wrapper.innerHTML = "";

  updateProductCount(products, productType);
  if (!currentSortColumn) {
    products.sort((a, b) => a.title.localeCompare(b.title));
  }

  const tagFields = getTagFieldsForType(productType);
  const processedTagFields = tagFields.flatMap((field) => {
    if (field.label === "Dims") {
      const isMousepad = productType === "Mousepads";
      return [
        {
          label: "Length (mm)",
          baseLabel: "Length",
          prefix: field.prefix,
          part: 0,
        },
        {
          label: "Width (mm)",
          baseLabel: "Width",
          prefix: field.prefix,
          part: 1,
        },
        {
          label: isMousepad ? "Thickness (mm)" : "Height (mm)",
          baseLabel: isMousepad ? "Thickness" : "Height",
          prefix: field.prefix,
          part: 2,
        },
        {
          label: "Area (cm²)",
          baseLabel: "Area",
          prefix: field.prefix,
          isDerived: true,
        }
      ];
    } else {
      return field;
    }
  });

  const defaultHeaders = [
    { label: "Favorite" },
    { label: "View" },
    { label: "Video" },
    { label: "Compare" },
    { label: "Match %" },
    { label: "Brand" },
    { label: "Name" },
    { label: "Reviews" },
    ...processedTagFields,
    { label: "Price" },
    { label: "Stock" },
    { label: "Date Added" },
  ];

  const savedOrder = loadLocal(`columnOrder-${productType}`);
  const fallbackOrder = defaultHeaders.map((h) => h.label);

  const fallbackSet = new Set(fallbackOrder);
  let columnOrder = [];

  if (savedOrder) {
    const cleanedSaved = savedOrder.filter((label) => fallbackSet.has(label));
    const missing = fallbackOrder.filter(
      (label) => !cleanedSaved.includes(label)
    );
    columnOrder = [...cleanedSaved, ...missing];
  } else {
    columnOrder = fallbackOrder;
  }

  window.allHeaders = columnOrder
    .map((label) => defaultHeaders.find((h) => h.label === label))
    .filter(Boolean);

  const fixedWidths = {
    View: "42px",
    Favorite: "42px",
    Reviews: "110px",
    Compare: "42px",
    Video: "42px",
    "Match %": "42px",
    Brand: "150px",
    Name: "400px",
    Price: "90px",
    Stock: "90px",
    "Date Added": "120px",
  };

  const MAX_COMPARE = 4;

  function updateCompareDisabled() {
    const rows = document.querySelectorAll(
      "#products-table-wrapper table tbody tr"
    );
    const selectedCount = selectedProductsForComparison.length;

    rows.forEach((row) => {
      if (
        selectedCount >= MAX_COMPARE &&
        !row.classList.contains("row-selected")
      ) {
        row.classList.add("row-disabled");
      } else {
        row.classList.remove("row-disabled");
      }
    });
  }

  const table = document.createElement("table");
  table.classList.add("fade-in");
  const thead = document.createElement("thead");
  const headerRow = document.createElement("tr");

  allHeaders.forEach((field) => {
    if (hiddenColumns.includes(field.label)) return;
    const th = document.createElement("th");

    if (fixedWidths[field.label]) {
      th.style.width = fixedWidths[field.label];
    } else {
      th.style.maxWidth = "350px";
      th.style.whiteSpace = "nowrap";
      th.style.textOverflow = "ellipsis";
    }

    th.innerHTML = `
  <span class="flex-container-1">
      ${
        ["View", "Favorite", "Video", "Compare"].includes(field.label)
          ? ""
          : tooltipHints[field.label]
            ? `<span class="db-tooltip-wrapper">
                 <span class="db-tooltip" data-tooltip-html="${escapeHTML(tooltipHints[field.label].replace(/\n/g, '<br>'))}">
                   ${field.label}
                 </span>
               </span>`
            : field.label
      }
    <div>
      ${
          !["Compare", "View", "Video", "Match %"].includes(field.label)
          ? `<button class="dropdown-toggle" data-column="${field.label}" class="styled-box-1"><span class="arrow">▼</span></button>`
          : ""
      }
      ${
          !["View", "Video", "Compare", "Favorite", "Match %"].includes(field.label)
          ? `<button class="sort-toggle" data-column="${field.label}" class="styled-box-1">⇅</button>`
          : ""
      }
    </div>
  </span>
  <span class="selected-filter" data-column="${
    field.label
  }" class="text-small-4"></span>
`;

    const resizer = document.createElement("div");
    resizer.className = "col-resizer";
    th.appendChild(resizer);
    headerRow.appendChild(th);
  });

  thead.appendChild(headerRow);
  table.appendChild(thead);

  thead.querySelectorAll("th").forEach((th) => {
    const resizer = th.querySelector(".col-resizer");
    if (!resizer) return;

    resizer.addEventListener("mousedown", function (e) {
      const startX = e.pageX;
      const startWidth = th.offsetWidth;

      const onMouseMove = (e) => {
        const newWidth = startWidth + (e.pageX - startX);
        th.style.width = `${newWidth}px`;
      };

      const onMouseUp = () => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    });
  });

  const tbody = document.createElement("tbody");
  tbody.id = "productsTableBody";

  const sameSet =
    lastRenderedProductSet === products ||
    (lastRenderedProductSet &&
      lastRenderedProductSet.length === products.length &&
      lastRenderedProductSet.every((p, i) => p.id === products[i].id));

  if (!sameSet) {
  Object.keys(window.productMap).forEach((k) => delete window.productMap[k]);
    products.forEach((product) => {
      const fullId = String(product.id);
      const numericId = fullId.match(/\d+$/)?.[0];

      window.productMap[fullId] = product;       // full gid
      if (numericId) {
        window.productMap[numericId] = product;  // numeric tail
      }
    });
    lastRenderedProductSet = products;
    window.allProducts = products;
  }

  products.forEach((product) => {
    const variant = product.variants.edges[0]?.node || {};
    const rawPrice = parseFloat(variant.price?.amount || 0);
    const price = rawPrice === 0 ? "N/A" : `£${rawPrice.toFixed(2)}`;
    const inStockIcon = variant.availableForSale
      ? `<img src="https://cdn.shopify.com/s/files/1/0102/8426/9615/files/arrow-up-circle_24x24_1x_1.svg?v=1602338544" alt="In Stock" title="In Stock">`
      : `<img src="https://cdn.shopify.com/s/files/1/0102/8426/9615/files/arrow-down-circle_24x24_1x_1.svg?v=1602338276" alt="Out of Stock" title="Out of Stock">`;

    const row = document.createElement("tr");
    if (product.productType === "Mousepads") {
      row.setAttribute("draggable", "true");
      row.addEventListener("dragstart", (e) => {
        e.dataTransfer.setData("text/plain", [product.id]);
        e.dataTransfer.effectAllowed = "copy";
      });
    }
    row.setAttribute("data-product-id", product.id);
    row.draggable = true;

    if (!row.hasAttribute("draggable")) {
      row.setAttribute("draggable", "true");
    }

    row.addEventListener("dragstart", (e) => {
      e.dataTransfer.setData("text/plain", product.id);
      e.dataTransfer.effectAllowed = "copy";
    });
    if (favoritedProducts.includes(product.id)) {
      row.classList.add("row-favorited");
    }
    if (selectedProductsForComparison.some((p) => p.id === product.id)) {
      row.classList.add("row-selected");
    }

   // expose the createdAt timestamp for the Date Added filter
   row.dataset.created = product.createdAt;

    // — immediately before row.innerHTML = … —
    const created = new Date(product.createdAt);
    const isNew = (Date.now() - created.getTime()) < 30*24*60*60*1000;

    row.innerHTML = window.allHeaders
      .map((field) => {
        if (hiddenColumns.includes(field.label)) return "";

        switch (field.label) {
          case "View":
            return `<td class="centered-text">
          <button class="view-btn"
            onclick='event.stopPropagation(); openProductModalById("${product.id}")'>
            <img src="https://cdn.shopify.com/s/files/1/0102/8426/9615/files/eye.svg?v=1753342185" alt="Quick View"/> </button>        
        </td>`;

          case "Favorite":
            const isFav = favoritedProducts.includes(product.id);
            return `<td class="centered-text">
          <button class="favorite-btn ${isFav ? "favorited" : ""}"
                  data-id="${product.id}">
            ${isFav ? "♥" : "♡"}
          </button>
        </td>`;

         case "Video":
          const ytTag = product.tags.find((tag) => tag.startsWith("YT01__"));
          const videoId = ytTag ? ytTag.replace("YT01__", "").trim() : null;
          return `
            <td class="centered-text">
              ${videoId ? 
                `<button class="video-btn" data-video-id="${videoId}" onclick="openVideoModal(event, '${videoId}')"><img src="https://cdn.shopify.com/s/files/1/0102/8426/9615/files/youtube_544252ba-8af9-48eb-88a1-747dd3e1281e.svg?v=1753350544"/></button>` 
                : ""}
            </td>`;

          case "Compare":
            return `<td class="centered-text"><div style="display: flex;">
          <input type="checkbox" class="compare-checkbox"
                 data-product-id="${product.id}"
                 onclick="event.stopPropagation();"
                 ${
                   selectedProductsForComparison.some(
                     (p) => p.id === product.id
                   )
                     ? "checked"
                     : ""
                 }>
          </div></td>`;

          case "Match %":
            if (selectedProductsForComparison.length === 2) {
              const [p1, p2] = selectedProductsForComparison;
              const isTarget = product.id === p1.id || product.id === p2.id;
              if (isTarget) {
                const percent = calculateProductMatchPercent(p1, p2);
                let color = "#ccc";
                if (percent >= 80) color = "#4caf50";
                else if (percent >= 50) color = "#ff9800";
                else color = "#f44336";
                return `<td class="centered-text">
              <span style="color: ${color}; font-weight: bold;">${percent}%</span>
            </td>`;
              }
            }
            return `<td class="centered-text">—</td>`;

          case "Brand":
            return `<td>${escapeHTML(getDisplayVendor(product.vendor))}</td>`;

          case "Name": {
            const brand = getDisplayVendor(product.vendor).trim().toLowerCase();
            let nameOnly = product.title.trim();

            const brandPattern = new RegExp(`^${brand}[:\\-\\s]*`, "i");
            nameOnly = nameOnly.replace(brandPattern, "").trim();

            const phrasesToStrip = [
              "mousepad","gaming mousepad","gaming mouse pad","mouse pad","anti-fray",
              "spill-proof","high-end","full desk","mouse","gaming","skatez","mouse feet","skates",
            ];
            phrasesToStrip.forEach((phrase) => {
              const plain = new RegExp(`\\b${phrase}\\b`, "ig");
              nameOnly = nameOnly.replace(plain, "");
              const bracketed = new RegExp(`[\\[\\(\\{][^\\]\\)\\}]*\\b${phrase}\\b[^\\[\\(\\{]*[\\]\\)\\}]`, "ig");
              nameOnly = nameOnly.replace(bracketed, "");
            });
            nameOnly = nameOnly.replace(/[\[\(\{]\s*[\]\)\}]/g, "");

            const created = new Date(product.createdAt);
            const isNew = (Date.now() - created.getTime()) < 30*24*60*60*1000;

          // numeric product id for summaries (same pattern used in Reviews)
          const pidForComments = String(product.id || '').match(/\d+$/)?.[0] || String(product.id || '');

          // inline, accessible comment icon; hidden until hydrated
          const commentIcon = `
            <button
              class="comment-icon-btn db-comments-icon"
              data-pid="${pidForComments}"
              title="Open comments"
              aria-label="Open comments"
              onclick='event.stopPropagation(); openProductModalById("${product.id}", { defaultTab: "comments" })'
              >
              <img
                src="${COMMENTS_ICON_URL}"
                alt=""
                width="18"
                height="18"
                loading="lazy"
                decoding="async"
                style="display:inline-block;vertical-align:middle;"
              />
              <span class="db-comments-count"></span>
            </button>`;

            return `<td>
            ${escapeHTML(nameOnly)}${commentIcon}
            ${isNew ? '<span class="new-badge">NEW</span>' : ''}
            </td>`;
          }

          case "Reviews": {
            const pidForReviews = String(product.id || '').match(/\d+$/)?.[0] || String(product.id || '');
            return `<td class="centered-text">
              <div class="db-reviews" data-pid="${pidForReviews}" title="Loading…" aria-label="Loading…">
                <div class="td-wrapper" data-value="0">
                  <span class="review-text">0.0</span>
                  <span class="review-star" style="color:#000;">★</span>
                  <span class="review-count">(0)</span>
                </div>
              </div>
            </td>`;
          }

          case "Date Added":
            // format as DD/MM/YYYY (British locale) – adjust as needed
            return `<td>${escapeHTML(
              created.toLocaleDateString("en-GB")
            )}</td>`;

          case "Price":
            return `<td>${price}</td>`;

          case "Stock":
            const stockVal = variant.availableForSale
              ? "In Stock"
              : "Out of Stock";
            return `<td>
        <div class="td-wrapper" data-value="${stockVal}">
          ${inStockIcon}
        </div>
      </td>`;

          case "Glide Type":
            let glideVal = "";
            const glideTag = product.tags.find((tag) =>
              tag.startsWith("Surface Type__")
            );
            if (glideTag) {
              glideVal = glideTag.replace("Surface Type__", "");
            }
            const chipClass =
              {
                Speed: "speed",
                Control: "control",
                Balanced: "balanced",
              }[glideVal] || "";
            return `<td>
          <span class="glide-chip ${chipClass}">
            ${escapeHTML(glideVal)}
          </span>
        </td>`;

          case "Firmness":
            let firmVal = "";
            const firmTag = product.tags.find((tag) =>
              tag.startsWith("Firmness__")
            );
            if (firmTag) firmVal = firmTag.replace("Firmness__", "");
            const firmClass =
              {
                "Extra Soft": "extra-soft",
                Soft: "soft",
                Mid: "mid",
                Hard: "hard",
              }[firmVal] || "";
            return `<td>
          <span class="firmness-chip ${firmClass}">
            ${escapeHTML(firmVal)}
          </span>
        </td>`;

          default:
            const fieldDef = processedTagFields.find(
              (f) => f.label === field.label
            );
            if (!fieldDef) return "<td></td>";

            const dimsFields = [
              "Length (mm)",
              "Width (mm)",
              "Height (mm)",
              "Thickness (mm)",
            ];
            if (field.label === "Area (cm²)") {
              const dimsTag = product.tags.find((t) => t.startsWith(field.prefix));
              if (dimsTag) {
                const dims = dimsTag
                  .replace(field.prefix, "")
                  .replace(/mm/gi, "")
                  .split(/[x×]/)
                  .map((s) => parseFloat(s.trim()));

                const [length, width] = dims;
                if (!isNaN(length) && !isNaN(width)) {
                  const area = (length * width) / 100; // convert to cm²
                  return `<td class="numeric-align"><div class="td-wrapper">${area.toFixed(0)}</div></td>`;
                }
              }
              return `<td><div class="td-wrapper"></div></td>`;
            }
            if (dimsFields.includes(field.label)) {
              const dimsTag = product.tags.find((tag) =>
                tag.startsWith(fieldDef.prefix)
              );
              const dims = dimsTag
                ? dimsTag
                    .replace(fieldDef.prefix, "")
                    .replace(/mm/gi, "")
                    .split(/[x×]/)
                    .map((v) => v.trim())
                : [];
              const dimVal = dims[fieldDef.part] || "";
              const isNumeric = /^-?\d+(\.\d+)?$/.test(dimVal.trim());
              const tdClass = isNumeric ? "numeric-align" : "";

              let displayVal = dimVal;
              if (field.label === "Thickness (mm)" && isNumeric) {
                displayVal = parseFloat(dimVal).toFixed(1);
              }

              return `<td class="${tdClass}">${escapeHTML(displayVal)}</td>`;
            }

            const tag = product.tags.find((tag) =>
              tag.startsWith(fieldDef.prefix)
            );
            let val = "";
            if (tag) {
              val = tag.replace(fieldDef.prefix, "");
            }
            const isNumeric = /^-?\d+(\.\d+)?$/.test(val.trim());
            const tdClass = isNumeric ? "numeric-align" : "";

            if (field.label.toLowerCase() === "colour" && val) {
const rawCols = val
  .split(/\s*•\s*|\s*,\s*|\s*\/\s*/)
  .map((s) => s.trim())
  .filter(Boolean);

const cssCols = rawCols.map(
  (label) => colourMap[label] || label.replace(/\s+/g, "").toLowerCase()
);

const blockHtml = cssCols
  .map(
    (color) =>
      `<div style="flex:1; background:${color}; height:100%;"></div>`
  )
  .join("");
              

return `<td>
  <div class="td-wrapper" data-value="${escapeHTML(val)}">
    <div class="colour-block" style="display:flex; height:18px; overflow:hidden; border-radius:4px;">
      ${blockHtml}
    </div>
  </div>
</td>`;

            }

            return `<td class="${tdClass}">
          <div class="td-wrapper">${escapeHTML(val)}</div>
        </td>`;
        }
      })
      .join("");

    row.addEventListener("click", (e) => {
      const viewBtn = e.target.closest(".view-btn");
      const favBtn = e.target.closest(".favorite-btn");
      if (viewBtn || favBtn) return;

      const checkbox = row.querySelector(".compare-checkbox");
      if (checkbox) {
        checkbox.checked = !checkbox.checked;
        checkbox.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });

    // ── delegate to our universal context‐menu ────────────────────────────
    row.addEventListener("contextmenu", (e) => {
      showProductContextMenu(e, product, products);
    });

    tbody.appendChild(row);
  });

  table.appendChild(tbody);
  wrapper.appendChild(table); 
  // Hydrate Reviews, then comment badges (only when API is ready)
 window.hydrateDbReviews(table);
 window.hydrateDbComments(table);

  document.querySelectorAll("td.highlight-diff").forEach((cell) => {
    cell.classList.remove("highlight-diff");
  });

  if (selectedProductsForComparison.length === 2) {
    highlightDiffColumns(
      selectedProductsForComparison[0],
      selectedProductsForComparison[1],
      processedTagFields
    );
  }
  setupDropdownFilters(table);
  updateURLFromFilters();
  window.dispatchEvent(new CustomEvent('db:table-rendered'));
  
  if (currentSortColumn) {
    const sortButton = table.querySelector(
      `.sort-toggle[data-column="${currentSortColumn}"]`
    );

    if (sortButton) {
      let colIndex = [...sortButton.closest("tr").children].indexOf(
        sortButton.closest("th")
      );
      const rows = Array.from(wrapper.querySelectorAll("tbody tr"));
      rows.sort((a, b) => {
        const getCellValue = (row) => {
          const cell = row.children[colIndex];
          if (!cell) return "";
          const wrapper = cell.querySelector(".td-wrapper");
          if (wrapper) {
            return (wrapper.dataset.value || wrapper.textContent).trim();
          }
          return cell.textContent.trim();
        };

        // special case: Date Added → use dataset.created, not cell text
        if (currentSortColumn === "Date Added") {
          const aDate = new Date(a.dataset.created);
          const bDate = new Date(b.dataset.created);
          return sortAsc ? aDate - bDate : bDate - aDate;
        }

        const aRaw = getCellValue(a);
        const bRaw = getCellValue(b);

        const aNum = parseFloat(aRaw.replace(/[^0-9.\-]/g, ""));
        const bNum = parseFloat(bRaw.replace(/[^0-9.\-]/g, ""));
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return sortAsc ? aNum - bNum : bNum - aNum;
        }

        const aText = aRaw.toLowerCase();
        const bText = bRaw.toLowerCase();
        return sortAsc
          ? aText.localeCompare(bText)
          : bText.localeCompare(aText);
      });
      const tbody = table.querySelector("tbody");
      rows.forEach((row) => tbody.appendChild(row));
    }
  }

  const searchBox = document.getElementById("quick-search-container");
  if (searchBox) {
    searchBox.style.display = "flex";
    searchBox.classList.add("fade-in");
  }

  table.querySelectorAll(".favorite-btn").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();

      btn.classList.add("pop-animation");
      setTimeout(() => btn.classList.remove("pop-animation"), 300);

      const id = btn.dataset.id;
      const row = btn.closest("tr");
      const isFav = favoritedProducts.includes(id);

      if (isFav) {
        favoritedProducts = favoritedProducts.filter((p) => p !== id);

        activeFavoriteName = null;
        renderSavedFavorites();

        btn.textContent = "♡";
        btn.classList.remove("favorited");
        if (row) row.classList.remove("row-favorited");
      } else {
        favoritedProducts.push(id);
        btn.textContent = "♥";
        btn.classList.add("favorited");
        if (row) row.classList.add("row-favorited");
      }
      saveLocal("favoritedProducts", favoritedProducts);
      document.dispatchEvent(new Event("favoritesUpdated"));
      updateFavoriteButtonsVisibility();
    });
  });

  const copyBtn = document.getElementById("copyShareLinkBtn");
  if (copyBtn) {
    copyBtn.onclick = copyCurrentUrlToClipboard;
  }

  const recentlyWrapper = document.getElementById("recently-viewed-wrapper");
  if (recentlyWrapper) recentlyWrapper.style.display = "block";
  updateFilterButtonsVisibility();
  updateCompareButtonsVisibility();
  document.querySelectorAll(".ui-after-table").forEach((el) => {
    el.style.display = "inline-flex";
  });

  initFullscreenToggle();

  const savedCompareContainer = document.getElementById(
    "savedComparisonsContainer"
  );
  if (savedCompareContainer)
    savedCompareContainer.style.display = "inline-flex";
  renderSavedComparisons();

const firstHeader = window.allHeaders.find(
  (h) => !hiddenColumns.includes(h.label)
);

const nonFilterable = ["Favorite", "View", "Video", "Compare", "Match %"];
if (firstHeader && nonFilterable.includes(firstHeader.label)) {
  const ths = table.querySelectorAll("thead th");
  const firstTh = ths[0];
  const dropdownBtn = firstTh?.querySelector(".dropdown-toggle");
  if (dropdownBtn) {
    dropdownBtn.style.display = "none";
  }
}

  table.addEventListener("change", (e) => {
    if (e.target.classList.contains("compare-checkbox")) {
      const id = e.target.dataset.productId;
      const product = products.find((p) => p.id === id);
      const row = e.target.closest("tr");

      e.target.classList.add("pop-animation");
      setTimeout(() => e.target.classList.remove("pop-animation"), 300);

      if (e.target.checked) {
        if (selectedProductsForComparison.length >= 4) {
          alert("You can only compare 4 products at a time.");
          e.target.checked = false;
          return;
        }
        selectedProductsForComparison.push(product);
        row.classList.add("row-selected");
      } else {
        selectedProductsForComparison = selectedProductsForComparison.filter(
          (p) => p.id !== id
        );
        row.classList.remove("row-selected");
      }

      activeComparisonName = null;
      renderSavedComparisons();

      saveComparisonToStorage();
      updateCompareButtonsVisibility();
      updateCompareTrayUI();
      updateMatchPercent();

      updateCompareDisabled();

      document
        .querySelectorAll("td.highlight-diff")
        .forEach((td) => td.classList.remove("highlight-diff"));

      if (selectedProductsForComparison.length === 2) {
        const [p1, p2] = selectedProductsForComparison;
        const tagFields = getTagFieldsForType(p1.productType);
        highlightDiffColumns(p1, p2, processedTagFields);
      }
    }
  });

  document.getElementById("clearSelectionBtn").addEventListener("click", () => {
    resetCompare();

    activeComparisonName = null;
    renderSavedComparisons();

    updateCompareTrayUI();
    updateCompareButtonsVisibility();

    document.querySelectorAll(".compare-checkbox").forEach((cb) => {
      cb.checked = false;
    });

    document.querySelectorAll("tr.row-selected").forEach((row) => {
      row.classList.remove("row-selected");
    });

    document
      .querySelectorAll("td.highlight-diff")
      .forEach((td) => td.classList.remove("highlight-diff"));

    document.querySelectorAll("td").forEach((td) => {
      if (td.textContent.includes("%") && td.title?.includes("Match")) {
        td.innerHTML = "—";
      }
    });

 // ── GRID VIEW: uncheck all overlay toggles & remove the compare highlight ──
 document
   .querySelectorAll("#productGridContainer .overlay-compare-toggle")
   .forEach((cb) => {
     cb.checked = false;
     const card = cb.closest(".product-card");
     if (card) card.classList.remove("selected-for-compare");
   });

    updateMatchPercent();
    updateCompareDisabled();
  });

  table.querySelectorAll(".sort-toggle").forEach((button) => {
    let sortAsc = true;
    button.addEventListener("click", () => {
      const column = button.dataset.column;
      const colIndex = [...button.closest("tr").children].indexOf(
        button.closest("th")
      );
      const rows = Array.from(wrapper.querySelectorAll("tbody tr"));

      sortAsc = currentSortColumn === column ? !currentSortAsc : true;

      currentSortColumn = column;
      currentSortAsc = sortAsc;

      table
        .querySelectorAll(".sort-toggle")
        .forEach((btn) => btn.classList.remove("sort-active"));
      button.classList.add("sort-active");

      rows.sort((a, b) => {
        // special case: Date Added → use dataset.created (ISO), not text
        if (currentSortColumn === "Date Added") {
          const aDate = new Date(a.dataset.created);
          const bDate = new Date(b.dataset.created);
          return sortAsc ? aDate - bDate : bDate - aDate;
        }

        const getCellValue = (row) => {
          const cell = row.children[colIndex];
          if (!cell) return "";
          const wrapper = cell.querySelector(".td-wrapper");
          if (wrapper) {
            return (wrapper.dataset.value || wrapper.textContent).trim();
          }
          return cell.textContent.trim();
        };

        const aRaw = getCellValue(a);
        const bRaw = getCellValue(b);

        const aNum = parseFloat(aRaw.replace(/[^0-9.\-]/g, ""));
        const bNum = parseFloat(bRaw.replace(/[^0-9.\-]/g, ""));
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return sortAsc ? aNum - bNum : bNum - aNum;
        }

        const aText = aRaw.toLowerCase();
        const bText = bRaw.toLowerCase();
        return sortAsc ? aText.localeCompare(bText) : bText.localeCompare(aText);
      });

      const tbody = table.querySelector("tbody");
      rows.forEach((row) => tbody.appendChild(row));
    });
  });

  const favToggle = document.getElementById("toggleFavoritesCheckbox");
  const favToggleContainer = document.querySelector(".switch-toggle");
  if (favToggle && favToggleContainer) {
    favToggleContainer.style.display = "inline-flex";

    favToggle.checked = showingFavoritesOnly;

    favToggle.addEventListener("change", () => {
      showingFavoritesOnly = favToggle.checked;
      applySidebarFilters();
      updateFilterButtonsVisibility();
    });
  }

  document.addEventListener("click", (e) => {
    const dropdown = document.getElementById("dropdown-menu-container");
    if (
      !dropdown.contains(e.target) &&
      !e.target.classList.contains("dropdown-toggle")
    ) {
      dropdown.style.display = "none";
      activeDropdownColumn = null;
    }
  });



}

document.addEventListener("click", () => {
  document.getElementById("rowContextMenu")?.remove();
  document
    .querySelectorAll("tr")
    .forEach((tr) => tr.classList.remove("context-highlight"));
});

function updateMatchPercent() {
  const matchIndex = window.allHeaders.findIndex((h) => h.label === "Match %");
  if (matchIndex === -1) return;

  if (selectedProductsForComparison.length !== 2) {
    [...document.querySelectorAll(`td:nth-child(${matchIndex + 1})`)].forEach(
      (cell) => {
        cell.innerHTML = "—";
      }
    );
    return;
  }

  if (selectedProductsForComparison.length !== 2) return;

  const [p1, p2] = selectedProductsForComparison;
  const tagFields = getTagFieldsForType(p1.productType);
  const percent = calculateProductMatchPercent(p1, p2);

  let color = "#ccc";
  if (percent >= 80) color = "#4caf50";
  else if (percent >= 50) color = "#ff9800";
  else color = "#f44336";

  [p1.id, p2.id].forEach((id) => {
    const row = document.querySelector(`tr[data-product-id="${id}"]`);
    if (!row) return;
    const cell = row.children[matchIndex];
    if (cell) {
      cell.innerHTML = `<span style="color: ${color}; font-weight: bold;">${percent}%</span>`;
    }
  });
}

function populateProductTypeFilter() {
  const container = document.getElementById("productTypeButtons");

  const typeButtonsHtml = allowedTypes
    .map((t) => {
      const icon = getTypeIcon(t);
      return `
        <div class="type-filter-container">
          <button class="type-filter-btn" data-type="${t}" title="${t}">
            ${icon ? `<img src="${icon}" alt="${t} icon" class="type-icon">` : ""}
          </button>
          <span class="type-filter-text">Mousepads</span>
        </div>
      `;
    })
    .join("");

  const extraButtonHtml = `
    <div class="type-filter-container">
      <button class="question-btn" disabled title="More coming soon">
        <span class="type-icon">?</span>
      </button>
      <span class="type-filter-text">Coming soon...</span>
    </div>
  `;

  container.innerHTML = typeButtonsHtml + extraButtonHtml;

// ── in Assets/db-products.js ──
document.querySelectorAll(".type-filter-btn").forEach((btn) => {
  btn.addEventListener("click", async () => {
    // ── 1) if it’s the same type, bail out immediately ──
    if (btn.dataset.type === currentProductType) return;

    // ── 2) show the loader & fade out the welcome text ──
    if (typeof window.showTableLoader === "function") {
      window.showTableLoader();
    }
    // (no need to call fadeOut(welcome) here — showTableLoader does that)

    // ── 3) now reset the compare UI ──
    resetCompare();
    document.getElementById("clearSelectionBtn").style.display = "none";
    updateCompareButtonsVisibility();

    // ── 4) switch the active button visually ──
    const type = btn.dataset.type;
    document
      .querySelectorAll(".type-filter-btn")
      .forEach((b) => b.classList.remove("active"));
    btn.classList.add("active");

    // ── 5) update state & fetch/render the new products ──
    currentProductType = type;
    const products = await fetchProductsByType(type);
    allProducts = products;
    currentFilteredProducts = products.filter(
      (p) => !p.tags.includes("Open Box")
    );
    renderProductsTable(currentFilteredProducts, type);
    buildSidebarFilterContents();
    bindSidebarToggleEvents();
    updateCompareButtonsVisibility();
    updateFavoriteButtonsVisibility();

    // ── 6) hide loader once the new table is in place ──
    if (typeof window.hideTableLoader === "function") {
      window.hideTableLoader();
    }
  });
});
}

function highlightDiffColumns(productA, productB, tagFields) {

  const matchableIds = new Set([productA.id, productB.id]);
  const columns = window.allHeaders;

  columns.forEach((field, colIndex) => {
    if (
      [
        "Favorite",
        "View",
        "Compare",
        "Match %",
        "Brand",
        "Name",
        "Price",
        "Stock",
      ].includes(field.label)
    ) {
      return;
    }

    const tagDef = tagFields.find((f) => f.label === field.label);
    if (!tagDef) {
      return;
    }
    if (
      ["Length (mm)", "Width (mm)", "Height (mm)", "Thickness (mm)"].includes(
        field.label
      )
    ) {
      const tagA = productA.tags.find((t) => t.startsWith(tagDef.prefix));
      const tagB = productB.tags.find((t) => t.startsWith(tagDef.prefix));
      const dimsA = tagA
        ? tagA
            .replace(tagDef.prefix, "")
            .replace(/mm/gi, "")
            .split(/[x×]/)
            .map((s) => s.trim())
        : [];
      const dimsB = tagB
        ? tagB
            .replace(tagDef.prefix, "")
            .replace(/mm/gi, "")
            .split(/[x×]/)
            .map((s) => s.trim())
        : [];

      const valA = dimsA[tagDef.part] || "";
      const valB = dimsB[tagDef.part] || "";

      if (valA !== valB) {
        document.querySelectorAll(`tr[data-product-id]`).forEach((row) => {
          const pid = row.getAttribute("data-product-id");
          if (!matchableIds.has(pid)) return;
          const cell = row.children[colIndex];
          if (cell) {
            cell.classList.add("highlight-diff");
          } else {
          }
        });
      }
    } else {
      const tagA = productA.tags.find((t) => t.startsWith(tagDef.prefix));
      const tagB = productB.tags.find((t) => t.startsWith(tagDef.prefix));
      const valA =
        tagA?.replace(tagDef.prefix, "").trim() || "";
      const valB =
        tagB?.replace(tagDef.prefix, "").trim() || "";

      if (valA !== valB) {
        document.querySelectorAll(`tr[data-product-id]`).forEach((row) => {
          const pid = row.getAttribute("data-product-id");
          if (!matchableIds.has(pid)) return;
          const cell = row.children[colIndex];
          if (cell) {
            cell.classList.add("highlight-diff");
          } else {
          }
        });
      }
    }
  });
}

function getCleanProductTitle(product) {
  const brand = getDisplayVendor(product.vendor).trim().toLowerCase();
  let nameOnly = product.title.trim();

  const brandPattern = new RegExp(`^${brand}[:\\-\\s]*`, "i");
  nameOnly = nameOnly.replace(brandPattern, "").trim();

  const phrasesToStrip = [
      "mousepad",
      "gaming mousepad",
      "gaming mouse pad",
      "mouse pad",
      "anti-fray",
      "spill-proof",
      "high-end",
      "full desk",
      "mouse",
      "gaming",
      "skatez",
      "mouse feet",
      "skates",
  ];

  phrasesToStrip.forEach((phrase) => {
    const plainPattern = new RegExp(`\\b${phrase}\\b`, "ig");
    nameOnly = nameOnly.replace(plainPattern, "");
    const bracketedPattern = new RegExp(
      `[\\[\\(\\{][^\\]\\)\\}]*\\b${phrase}\\b[^\\[\\(\\{]*[\\]\\)\\}]`,
      "ig"
    );
    nameOnly = nameOnly.replace(bracketedPattern, "");
  });

  nameOnly = nameOnly.replace(/[\[\(\{]\s*[\]\)\}]/g, "");

  return `${getDisplayVendor(product.vendor)} ${nameOnly.trim()}`;
}

// Call this to close the video modal and clean up
function closeVideoModal() {
  const videoModal = document.getElementById("videoModal");
  const videoFrame = document.getElementById("videoFrame");
  videoModal.style.display = "none";
  videoFrame.src = "";
  document.removeEventListener("keydown", onVideoModalKeydown);
}

// Keydown handler to listen for Escape
function onVideoModalKeydown(e) {
  if (e.key === "Escape" || e.key === "Esc" || e.keyCode === 27) {
    closeVideoModal();
  }
}

// Call this to open the video modal
function openVideoModal(event, videoId) {
  event.stopPropagation();

  // Load the video
  const videoFrame = document.getElementById("videoFrame");
  videoFrame.src = `https://www.youtube.com/embed/${videoId}`;

  // Show & focus the modal so it can capture key events
  const videoModal = document.getElementById("videoModal");
  videoModal.style.display = "block";
  videoModal.setAttribute("tabindex", "-1");
  videoModal.focus();

  // Listen for Escape
  document.addEventListener("keydown", onVideoModalKeydown);
}

// ─── Hover‐for‐Preview in Table View (Row Trigger, after render) ─────────────────────────────
;(function(){
  const preview = document.createElement('div');
  preview.id = 'rowImagePreview';
  preview.style.display = 'none';
  Object.assign(preview.style, {
    position: 'fixed',
    bottom: '0',
    right: '0',
    zIndex: '9999',
    background: '#fff',
    padding: '6px',
    border: '1px solid #ccc',
    borderRadius: '6px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  });

  const img = document.createElement('img');
  Object.assign(img.style, {
    maxHeight: '200px',
    display:   'block',
    margin:    '0 auto'
  });
  preview.appendChild(img);

  const caption = document.createElement('div');
  Object.assign(caption.style, {
    textAlign: 'center',
    fontSize: '14px',
    color: '#000',
    fontWeight: '500',
    maxWidth: '250px',
    padding: '6px'
  });
  preview.appendChild(caption);

  document.body.appendChild(preview);

  let hoverTimer;

  // hide when leaving preview
  preview.addEventListener('mouseleave', () => {
    clearTimeout(hoverTimer);
    preview.style.display = 'none';
  });

  // bind only after table has rendered (so productMap is ready)
  window.addEventListener('db:table-rendered', () => {
    const tbody = document.querySelector('#productsTableBody');
    if (!tbody || tbody._hoverBound) return;
    tbody._hoverBound = true;

    tbody.addEventListener('mouseover', e => {
      const trEl = e.target.closest('tr[data-product-id]');
      if (!trEl) return;

      clearTimeout(hoverTimer);
      hoverTimer = setTimeout(() => {
        const pid = trEl.dataset.productId;
        const numericId = String(pid).match(/\d+$/)?.[0];
        const product =
          (window.productMap && window.productMap[pid]) ||
          (numericId && window.productMap && window.productMap[numericId]);

        if (!product) {
          console.warn('[rowHoverPreview] no product found for', pid, 'numeric', numericId,
                       'map keys:', Object.keys(window.productMap || {}));
          return;
        }

        const url = product.featuredImage?.url;
        if (!url) return;

        img.src = url;
        caption.textContent =
          typeof window.getCleanProductTitle === 'function'
            ? window.getCleanProductTitle(product)
            : (product?.title || '');

        preview.style.display = 'flex';

        // hide when leaving the row
        trEl.addEventListener('mouseleave', () => {
          clearTimeout(hoverTimer);
          preview.style.display = 'none';
        }, { once: true });
      }, 500);
    });
  });
})();

// Close when the close button is clicked
document.addEventListener("click", (e) => {
  if (e.target && e.target.id === "closeModalBtn") {
    closeVideoModal();
  }
});

// put this near the bottom of db-products.js (outside renderProductsTable)
window.hydrateDbReviews = async function(root = document) {
  if (!window.DB_REVIEWS || !window.DB_REVIEWS.getSummary) {
    window.addEventListener('gd:reviews-api-ready', () => window.hydrateDbReviews(root), { once: true });
    return;
  }

  const CACHE = (window.__reviewCache ||= new Map()); // pid -> {avg,count}
  const cells = Array.from(root.querySelectorAll('.db-reviews[data-pid]'));
  if (!cells.length) {
    window.dispatchEvent(new CustomEvent('db:reviews-hydrated'));
    return;
  }

  function paint(el, avg = 0, count = 0) {
    const wrap = el.querySelector('.td-wrapper');
    if (!wrap) return;
    const avg1 = (avg || 0).toFixed(1);
    const avg2 = (avg || 0).toFixed(2);
    const has = (count || 0) > 0 && (avg || 0) > 0;
    const starColor = has ? '#f5b50a' : '#000';
    wrap.innerHTML = `
      <span class="review-text">${avg1}</span>
      <span class="review-star" style="color:${starColor};">★</span>
      <span class="review-count">(${count || 0})</span>
    `;
    wrap.dataset.value = avg2;
    wrap.dataset.count = String(count || 0);
    wrap.dataset.filter =
      (count || 0) === 0 ? "No reviews" :
      avg >= 4.5 ? "4.5★+" :
      avg >= 4.0 ? "4★+"   :
      avg >= 3.5 ? "3.5★+" :
      avg >= 3.0 ? "3★+"   : "<3★";
    el.title = `${avg1} out of 5 stars based on ${count || 0} review${(count||0)===1?'':'s'}`;
    el.setAttribute('aria-label', el.title);
    el.dataset.revLoaded = '1';
    window.dispatchEvent(new CustomEvent('db:review-cell-painted', { bubbles: false }));
  }

    const toObserve = [];
    for (const el of cells) {
      const pid = el.getAttribute('data-pid');
      if (!pid) continue;
      // If we already fully painted this cell on a prior run, skip
      if (el.dataset.revLoaded === '1') continue;

      const cached = CACHE.get(pid);
      if (cached) {
        paint(el, cached.avg, cached.count);
        el.dataset.revLoaded = '1';
      } else if (!el.dataset.revObserved) {
        // Only observe once across re-renders
        toObserve.push(el);
      }
    }
    if (!toObserve.length) {
      window.dispatchEvent(new CustomEvent('db:reviews-hydrated'));
      return;
    }

    const queue = [];
    const seen  = new Set();
    const io = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const el = entry.target;
      const host = el.closest('[data-product-id]') || el;
      if (!host || host.offsetParent === null) continue;
      const pid = el.getAttribute('data-pid');
      if (!pid || seen.has(pid)) continue;
      seen.add(pid);
      queue.push({ el, pid });
      io.unobserve(el);
    }
    schedule();
  }, { root: document.querySelector('#tableContainer') || null, rootMargin: '300px 0px', threshold: 0 });

  toObserve.forEach(el => {
    io.observe(el);
    el.dataset.revObserved = '1';
  });

  const MAX_CONCURRENCY = 2;  // ← was 32
  let active = 0;
  let rafScheduled = false;

  function schedule() {
    if (rafScheduled) return;
    rafScheduled = true;
    requestAnimationFrame(step);
  }

  function step() {
    rafScheduled = false;
    let budget = 2; // up to 2 per frame
    while (budget > 0 && queue.length && active < MAX_CONCURRENCY) {
      budget--;
      const { el, pid } = queue.shift();
      const cached = CACHE.get(pid);
      if (cached) { paint(el, cached.avg, cached.count); continue; }

      active++;
      (async () => {
        try {
          const { avg, count } = await window.DB_REVIEWS.getSummary(pid);
          CACHE.set(pid, { avg, count });
          paint(el, avg, count);
        } finally {
          active--;
          if (queue.length) schedule();
        }
      })();
    }
    if (queue.length) schedule();
  }

  schedule();
};

// ======= Comments summary (single fetch like Reviews) =======

// Where the single JSON lives (set in theme.liquid; falls back to a static file)
const COMMENTS_SUMMARY_URL =
  (typeof window !== 'undefined' && window.COMMENTS_SUMMARY_URL) ||
  'comments-summary.v1.json';

let _cmIdx = null;
let _cmLoadPromise = null;
let _cmLoadedAt = 0;
const CM_TTL_MS = 10 * 60 * 1000; // refresh occasionally

function normalizePid(pid) {
  const s = String(pid || '');
  const m = s.match(/\d+$/);
  return m ? m[0] : s;
}

async function loadCommentIndexOnce() {
  if (_cmIdx && (Date.now() - _cmLoadedAt) < CM_TTL_MS) return _cmIdx;
  if (_cmLoadPromise) return _cmLoadPromise;

  _cmLoadPromise = (async () => {
    try {
      const res = await fetch(COMMENTS_SUMMARY_URL, { method: 'GET', credentials: 'omit' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      _cmIdx = (json && (json.items || json)) || {};
      _cmLoadedAt = Date.now();
      return _cmIdx;
    } catch (e) {
      console.warn('[db-products] comments summary load failed', e);
      _cmIdx = _cmIdx || {};
      return _cmIdx;
    } finally {
      _cmLoadPromise = null;
    }
  })();

  return _cmLoadPromise;
}

window.DB_COMMENTS = window.DB_COMMENTS || {};

/** Returns number of comments for a productId from the single JSON. */
window.DB_COMMENTS.getCount = async function(productId) {
  const pid = normalizePid(productId);
  if (!pid) return 0;
  const idx = await loadCommentIndexOnce();
  const e = idx[pid];
  return e ? Number(e.count) || 0 : 0;
};

/** Force-update local summary + repaint any visible icons for a PID. */
window.DB_COMMENTS.setLocalCount = function(productId, count) {
  const pid = normalizePid(productId);
  if (!pid) return;
  _cmIdx = _cmIdx || {};
  _cmIdx[pid] = { count: Number(count) || 0 };

  // repaint any visible placeholders
  document.querySelectorAll(`.db-comments-icon[data-pid="${pid}"]`).forEach((el) => {
    const n = Number(count) || 0;
    if (n > 0) {
      el.style.display = 'inline-flex';
      el.title = `${n} comment${n === 1 ? '' : 's'}`;
      el.setAttribute('aria-label', el.title);
    } else {
      el.style.display = 'none';
    }
  });
};

/** Hydrate comment icons under a container/table */
window.hydrateDbComments = async function(root = document) {
  const nodes = Array.from(root.querySelectorAll('.db-comments-icon[data-pid]'));
  if (!nodes.length) return;

  const idx = await loadCommentIndexOnce();
  for (const el of nodes) {
    const pid = el.getAttribute('data-pid');
    const entry = idx[pid];
    const n = entry ? Number(entry.count) || 0 : 0;
    if (n > 0) {
      el.style.display = 'inline-flex';
      el.title = `${n} comment${n === 1 ? '' : 's'}`;
      el.setAttribute('aria-label', el.title);

      const countSpan = el.querySelector('.db-comments-count');
      if (countSpan) countSpan.textContent = `(${n})`;
    } else {
      el.style.display = 'none';
    }
  }
};

// Optional: real-time updates if your comments UI dispatches these events
window.addEventListener('gd:comment-added', (e) => {
  const pid = normalizePid(e?.detail?.productId);
  if (!pid) return;
  const current = (_cmIdx?.[pid]?.count) || 0;
  window.DB_COMMENTS.setLocalCount(pid, current + 1);
});
window.addEventListener('gd:comment-removed', (e) => {
  const pid = normalizePid(e?.detail?.productId);
  if (!pid) return;
  const current = (_cmIdx?.[pid]?.count) || 0;
  window.DB_COMMENTS.setLocalCount(pid, Math.max(0, current - 1));
});
